import React, { useEffect, useMemo } from 'react';
import { Button, Upload } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
// Styles
import styles from './CoverImageInput.module.scss';

export const COVER_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const MAX_COVER_IMAGE_MB = 10;

type CoverImageInputProps = {
  file: File | null;
  onChange: (file: File | null) => void;
  currentImageUrl?: string | null;
  onError: (message: string) => void;
};

const CoverImageInput = ({
  file,
  onChange,
  currentImageUrl,
  onError,
}: CoverImageInputProps): JSX.Element => {
  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : currentImageUrl || null),
    [file, currentImageUrl]
  );

  useEffect(
    () => () => {
      if (file && previewUrl) {
        URL.revokeObjectURL?.(previewUrl);
      }
    },
    [file, previewUrl]
  );

  const handleBeforeUpload = (next: File): boolean => {
    if (!COVER_IMAGE_TYPES.includes(next.type)) {
      onError('Use a PNG, JPEG or WebP image.');
    } else if (next.size > MAX_COVER_IMAGE_MB * 1024 * 1024) {
      onError(`The image must be under ${MAX_COVER_IMAGE_MB} MB.`);
    } else {
      onChange(next);
    }

    // Uploaded on save, together with the event
    return false;
  };

  return (
    <div className={styles.root}>
      <div className={styles.preview}>
        {previewUrl ? (
          <img src={previewUrl} alt="Event cover" />
        ) : (
          <span className={styles.placeholder}>No cover yet</span>
        )}
      </div>
      <div className={styles.actions}>
        <Upload
          accept={COVER_IMAGE_TYPES.join(',')}
          showUploadList={false}
          beforeUpload={handleBeforeUpload}
        >
          <Button icon={<UploadOutlined />}>
            {previewUrl ? 'Replace cover' : 'Upload cover'}
          </Button>
        </Upload>
        {file && (
          <Button
            icon={<DeleteOutlined />}
            onClick={() => onChange(null)}
            aria-label="Remove the new cover"
          >
            {currentImageUrl ? 'Keep current' : 'Remove'}
          </Button>
        )}
      </div>
      <span className={styles.hint}>
        PNG, JPEG or WebP, up to {MAX_COVER_IMAGE_MB} MB. A 4:5 poster looks
        best on web.
      </span>
    </div>
  );
};

export default CoverImageInput;
